import { useState, useRef, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { FilterFunnel01 } from "untitledui-js/react";

import { Button, Typography } from "@pondigitalsolutions/pon-component-library";
import { useGetVacancyFilters } from "@hooks/useGetVacancyFilters";
import { Loader } from "./Loader";
import { useCurrentLang } from "@hooks/useCurrentLang";

export const VacanciesFilter = () => {
  const { lang } = useParams();
  const { t } = useTranslation();
  const currentLang = useCurrentLang(lang);

  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const {
    data: vacancyFilters,
    isLoading,
    error,
  } = useGetVacancyFilters({
    locale: currentLang,
  });

  return (
    <aside className="relative bg-tertiary-100 px-8 py-6 lg:py-8 dark:bg-secondary-900">
      <Button
        variant="primary"
        id="skip-filters"
        size="md"
        href="#vacancies-overview"
        className="sr-only !absolute top-9 right-9 hidden focus:not-sr-only focus:p-1 lg:block"
      >
        {t("VacanciesFilter.skipFilters")}
      </Button>
      <Typography
        as="h2"
        size="xl"
        className="mb-4 hidden font-semibold md:text-2xl lg:block dark:text-white"
        data-testid="vacancies-filter-heading"
      >
        {t("VacanciesFilter.filters")}
      </Typography>
      <button
        onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
        className="flex w-full items-center gap-2 text-xl font-semibold md:text-2xl lg:hidden"
      >
        <span>{t("VacanciesFilter.filters")}</span>
        <FilterFunnel01 />
      </button>
      {isLoading && <Loader />}
      {error && <Typography>{t("VacanciesFilter.errorLoading")}</Typography>}

      {!isLoading && !error && vacancyFilters && (
        <div
          className={twMerge(
            "flex-col gap-4",
            isMobileFiltersOpen ? "mt-4 flex lg:mt-0" : "hidden lg:flex"
          )}
        >
          {vacancyFilters.Bedrijf && (
            <VacancyFilter
              filterTitle={t("VacanciesFilter.filterCompany")}
              filterValues={vacancyFilters.Bedrijf.values}
              searchParamKey="company"
            />
          )}
          {vacancyFilters.department && (
            <VacancyFilter
              filterTitle={t("VacanciesFilter.filterDepartment")}
              filterValues={vacancyFilters.department.values}
              searchParamKey="department"
            />
          )}
          {vacancyFilters.city && (
            <VacancyFilter
              filterTitle={t("VacanciesFilter.filterCity")}
              filterValues={vacancyFilters.city.values}
              searchParamKey="city"
            />
          )}
          {vacancyFilters.region && (
            <VacancyFilter
              filterTitle={t("VacanciesFilter.filterRegion")}
              filterValues={vacancyFilters.region.values}
              searchParamKey="region"
            />
          )}
          {vacancyFilters.countryCode && (
            <VacancyFilter
              filterTitle={t("VacanciesFilter.filterCountry")}
              filterValues={vacancyFilters.countryCode.values}
              searchParamKey="countryCode"
            />
          )}
        </div>
      )}
    </aside>
  );
};

interface VacancyFilterProps {
  filterTitle: string;
  filterValues: { value: string; count: number }[];
  searchParamKey: string;
}

const VacancyFilter = ({
  filterTitle,
  filterValues,
  searchParamKey,
}: VacancyFilterProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAll, setShowAll] = useState(false);
  const firstHiddenItemRef = useRef<HTMLLIElement>(null);
  const justExpanded = useRef(false);

  useEffect(() => {
    // Focus on the first newly revealed item after expanding
    if (justExpanded.current && showAll && firstHiddenItemRef.current) {
      const checkbox = firstHiddenItemRef.current.querySelector("input");
      checkbox?.focus();
      justExpanded.current = false;
    }
  }, [showAll]);

  const getSelectedValues = (): string[] => {
    const paramValue = searchParams.get(searchParamKey);
    return paramValue ? paramValue.split(",").map(decodeURIComponent) : [];
  };

  const handleCheckboxChange = (value: string, isChecked: boolean) => {
    // Get current selected values from URL parameters.
    const selectedValues = getSelectedValues();

    let updatedValues: string[];
    if (isChecked) {
      // Add value if not already present (prevents duplicates).
      updatedValues = selectedValues.includes(value)
        ? selectedValues
        : [...selectedValues, value];
    } else {
      // Remove value from selected list when unchecked.
      updatedValues = selectedValues.filter((v) => v !== value);
    }

    // Create new URLSearchParams object to modify search parameters.
    const newSearchParams = new URLSearchParams(searchParams);

    if (updatedValues.length > 0) {
      // Set parameter with comma-separated values, encoding each value.
      newSearchParams.set(
        searchParamKey,
        updatedValues.map(encodeURIComponent).join(",")
      );
    } else {
      // Remove parameter entirely when no values are selected.
      // This keeps URLs clean and prevents empty filter parameters.
      newSearchParams.delete(searchParamKey);
    }

    // Reset pagination to page 1 when filters change.
    newSearchParams.set("p", "1");

    // Update URL.
    setSearchParams(newSearchParams);
  };

  const selectedValues = getSelectedValues();
  const shouldShowToggle = filterValues.length > 5;
  const displayedValues = showAll ? filterValues : filterValues.slice(0, 5);

  const handleShowMoreClick = () => {
    if (!showAll) {
      justExpanded.current = true;
    }
    setShowAll(!showAll);
  };

  return (
    <fieldset>
      <legend className="font-semibold">{filterTitle}</legend>
      <ul>
        {displayedValues.map((filterValue, index) => {
          const checkboxId = `${searchParamKey}-${filterValue.value}`;
          const isFirstHiddenItem = index === 5;

          return (
            <li
              key={filterValue.value}
              className="flex items-center"
              ref={isFirstHiddenItem ? firstHiddenItemRef : null}
            >
              <input
                id={checkboxId}
                type="checkbox"
                checked={selectedValues.includes(filterValue.value)}
                onChange={(e) =>
                  handleCheckboxChange(filterValue.value, e.target.checked)
                }
              />
              <label
                htmlFor={checkboxId}
                className="cursor-pointer pl-2 text-sm text-tertiary-900 dark:text-tertiary-100"
              >
                {filterValue.value} ({filterValue.count})
              </label>
            </li>
          );
        })}
      </ul>
      {shouldShowToggle && (
        <>
          <Typography id={`${searchParamKey}-toggle-hint`} className="sr-only">
            {t("VacanciesFilter.screenReaderInstructions", {
              filterTitle,
            })}
          </Typography>

          <button
            type="button"
            aria-controls={`${searchParamKey}-options`}
            aria-expanded={showAll}
            onClick={handleShowMoreClick}
            className="cursor-pointer text-sm text-primary-700 underline hover:text-primary-500 dark:text-primary-500 hover:dark:text-primary-400"
          >
            {showAll
              ? t("VacanciesFilter.showLess")
              : t("VacanciesFilter.showMore")}
          </button>
        </>
      )}
    </fieldset>
  );
};
